import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { Button } from 'src/domains/common/components/button/button';
import { Input } from 'src/domains/common/components/input/input';

import { Switch } from 'src/domains/common/components/switch/switch';
import { Select } from 'src/domains/common/components/select/select';
import { BetTickerFormState } from './BetTickerForm.state';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { CompetitionPicker } from './CompetitionPicker/CompetitionPicker';
import { useCommon } from 'src/AppState/Common';
import { cn } from 'src/domains/common/components/utils';
import { ToasterState } from 'src/AppState/ToasterState';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { CompetitionStoreState } from './CompetitionPicker/CompetitionStore.state';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { SportsListType } from 'src/AppState/sdk/staff';
import { ConfigType, RulesType } from 'src/domains/sportsbook/trading/view/bet-ticker/BetTickerBox/config';

interface BetTickerFormPropType {
    onClose: () => void;
    onSubmit: (rules: RulesType, config: ConfigType) => void;
    store: AutoMap<string, CompetitionStoreState>;
    sportsResource: Resource<SportsListType>;
}

const stakeFactorConditions = ['<', '>', '=', '<=', '>='];

export const BetTickerForm = observer('BetTickerForm', (props: BetTickerFormPropType) => {
    const { onClose, onSubmit, store, sportsResource } = props;
    const common = useCommon();
    const [state] = React.useState(() => new BetTickerFormState(store, sportsResource));
    const [shouldCollapse, setShouldCollapse] = React.useState(false);
    const {
        filterBetsAbove,
        filterStakeFactorCondition,
        filterStakeFactor,
        rejectedBets,
        highlightBetsAbove,
        highlightStakeFactorCondition,
        highlightStakeFactor,
        highlightPercentageMaxStakeAbove,
        desktopOnlyBets,
        mobileOnlyBets,
        quickbetOnlyBets,
        telebettingOnlyBets,
        desktopOnlyColour,
        mobileOnlyColour,
        quickbetOnlyColour,
        telebettingOnlyColour,
        highlightBetsAboveColour,
        highlightStakeFactorColour,
        highlightPercentageMaxStakeAboveColour,
        competitionPickerState,
        isFormValid,
    } = state;

    const filterRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const checkHeight = (): void => {
            if (filterRef.current !== null) {
                const width = parseInt(window.getComputedStyle(filterRef.current).width);
                setShouldCollapse(width <= 690);
            }
        };

        checkHeight();
        window.addEventListener('resize', checkHeight);

        return (): void => window.removeEventListener('resize', checkHeight);
    }, []);

    return (
        <div className='bg-muted rounded-lg p-4 gap-4 flex flex-col h-full overflow-y-auto'>
            <div className='flex justify-between items-center'>
                <span className='text-base font-semibold'>Custom</span>
                <IconButton
                    icon='x'
                    variant='link'
                    size='sm'
                    className='text-foreground'
                    onClick={onClose}
                />
            </div>
            {/* change the cols based on width of filter and special highlights */}
            <div
                className={cn('grid gap-4', shouldCollapse ? 'grid-cols-1' : 'grid-cols-2')}
                ref={filterRef}
            >
                <div className='flex flex-col rounded-md bg-background p-6 gap-6 shadow-sm'>
                    <span className='text-base font-semibold'>Choose Filters</span>
                    <div className='flex gap-4 items-start'>
                        <Input
                            formInputState={filterBetsAbove}
                            errors={filterBetsAbove.errorsForView}
                            label='Bets Above (£)'
                            placeholder='Type'
                        />
                        <div className='flex gap-4 mt-7'>
                            <div className='bg-border w-[1px] shrink-0 h-9' />
                            <div className='flex rounded-md border border-input border-solid shadow-sm py-1 px-3 items-center h-9 min-w-[164px]'>
                                <Switch
                                    checked={rejectedBets.value}
                                    onChange={(e) => rejectedBets.setValue(e.target.checked)}
                                    label='Rejected Bets'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-2'>
                        <Select
                            label='Condition'
                            triggerClassName='min-w-[128px]'
                            errors={[]}
                            formInputState={filterStakeFactorCondition}
                            values={stakeFactorConditions.map((sfc) => {
                                return {
                                    value: sfc,
                                    label: sfc,
                                };
                            })}
                            placeholder='<='
                        />
                        <Input
                            label='Stake Factor'
                            formInputState={filterStakeFactor}
                            errors={filterStakeFactor.errorsForView}
                            placeholder='Type'
                        />
                    </div>
                    <CompetitionPicker competitionPickerState={competitionPickerState} />
                </div>
                <div className='flex flex-col rounded-md bg-background p-6 gap-6 shadow-sm'>
                    <span className='text-base font-semibold'>Special Highlights</span>
                    <div className='flex gap-2 items-start'>
                        <Input
                            formInputState={highlightBetsAbove}
                            errors={highlightBetsAbove.errorsForView}
                            label='Bets Above (£)'
                            placeholder='Type'
                        />
                        <div className='flex h-9 items-center mt-7'>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={highlightBetsAboveColour.value}
                                    onChange={(e): void => highlightBetsAboveColour.setValue(e.target.value)}
                                    onBlur={highlightBetsAboveColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-2 items-start'>
                        <Select
                            label='Condition'
                            triggerClassName='min-w-[128px]'
                            errors={[]}
                            formInputState={highlightStakeFactorCondition}
                            values={stakeFactorConditions.map((sfc) => {
                                return {
                                    value: sfc,
                                    label: sfc,
                                };
                            })}
                            placeholder='<='
                        />
                        <Input
                            label='Stake Factor'
                            formInputState={highlightStakeFactor}
                            errors={highlightStakeFactor.errorsForView}
                            placeholder='Type'
                        />
                        <div className='flex h-9 items-center mt-7'>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={highlightStakeFactorColour.value}
                                    onChange={(e): void => highlightStakeFactorColour.setValue(e.target.value)}
                                    onBlur={highlightStakeFactorColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-2 items-start'>
                        <Input
                            formInputState={highlightPercentageMaxStakeAbove}
                            errors={highlightPercentageMaxStakeAbove.errorsForView}
                            label='% Max Stake Above'
                            placeholder='Type'
                        />
                        <div className='flex h-9 items-center mt-7'>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={highlightPercentageMaxStakeAboveColour.value}
                                    onChange={(e): void =>
                                        highlightPercentageMaxStakeAboveColour.setValue(e.target.value)
                                    }
                                    onBlur={highlightPercentageMaxStakeAboveColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-col p-3 gap-4 rounded-lg border border-border border-solid'>
                        <span className='text-sm'>Bet Type</span>
                        <div className='flex justify-between rounded-md border border-input border-solid shadow-sm py-1 px-3 items-center h-9'>
                            <div className='flex gap-3'>
                                <Switch
                                    checked={desktopOnlyBets.value}
                                    onChange={(e) => desktopOnlyBets.setValue(e.target.checked)}
                                />
                                <span className='text-sm'>Desktop Only</span>
                            </div>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={desktopOnlyColour.value}
                                    onChange={(e): void => desktopOnlyColour.setValue(e.target.value)}
                                    onBlur={desktopOnlyColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>

                        <div className='flex justify-between rounded-md border border-input border-solid shadow-sm py-1 px-3 items-center h-9'>
                            <div className='flex gap-3'>
                                <Switch
                                    checked={mobileOnlyBets.value}
                                    onChange={(e) => mobileOnlyBets.setValue(e.target.checked)}
                                />
                                <span className='text-sm'>Mobile Only</span>
                            </div>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={mobileOnlyColour.value}
                                    onChange={(e): void => mobileOnlyColour.setValue(e.target.value)}
                                    onBlur={mobileOnlyColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                        <div className='flex justify-between rounded-md border border-input border-solid shadow-sm py-1 px-3 items-center h-9'>
                            <div className='flex gap-3'>
                                <Switch
                                    checked={quickbetOnlyBets.value}
                                    onChange={(e) => quickbetOnlyBets.setValue(e.target.checked)}
                                />
                                <span className='text-sm'>Quickbet Only</span>
                            </div>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={quickbetOnlyColour.value}
                                    onChange={(e): void => quickbetOnlyColour.setValue(e.target.value)}
                                    onBlur={quickbetOnlyColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                        <div className='flex justify-between rounded-md border border-input border-solid shadow-sm py-1 px-3 items-center h-9'>
                            <div className='flex gap-3'>
                                <Switch
                                    checked={telebettingOnlyBets.value}
                                    onChange={(e) => telebettingOnlyBets.setValue(e.target.checked)}
                                />
                                <span className='text-sm'>Telebetting Only</span>
                            </div>
                            <div className='flex overflow-hidden w-6 h-6 rounded-lg items-center justify-center border border-border border-solid'>
                                <input
                                    className='p-0 w-9 h-9 m-0 flex-shrink-0'
                                    value={telebettingOnlyColour.value}
                                    onChange={(e): void => telebettingOnlyColour.setValue(e.target.value)}
                                    onBlur={telebettingOnlyColour.setAsVisited}
                                    type='color'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='flex justify-end'>
                <Button
                    variant='primary'
                    className='h-9'
                    onClick={() => {
                        if (isFormValid()) {
                            onSubmit(state.rules, state.config);
                        } else {
                            ToasterState.get(common).setToast({
                                variant: 'error',
                                title: 'Error!',
                                message:
                                    'The bet ticker could not be started. Check that fields are filled out correctly.',
                            });
                        }
                    }}
                >
                    Start Ticker
                </Button>
            </div>
        </div>
    );
});
